import type { ComponentProps, FC } from "react";
import { Button, Card, Select } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import { XStack, YStack } from "tamagui";

import { api } from "~/utils/api";
import { getFractionalWidth } from "~/utils/get-fractional-width";
import { notUndefined } from "~/utils/not-undefined";
import {
  EMPTY_VAULE,
  groupByRange,
  patientFilterAges,
  patientFilterByLocations,
  patientGenders,
} from "../options";
import { useGeneralFilter } from "./context";

const useGetGeneralFilters = (withGroupBy: boolean) => {
  const [data] = api.group.getGroupNamesWithId.useSuspenseQuery();

  const filters = [
    {
      name: "groupId" as const,
      title: "Група",
      options: [
        { name: "Уся компанія", value: EMPTY_VAULE },
        ...data.map(({ name, id }) => {
          return { name: name ?? "", value: id ?? "" };
        }),
      ],
    },

    {
      name: "age" as const,
      title: "Вік",
      options: patientFilterAges,
    },
    {
      name: "gender" as const,
      title: "Стать",
      options: patientGenders,
    },
    {
      name: "area" as const,
      title: "Локація",
      options: patientFilterByLocations,
    },
    withGroupBy
      ? {
          name: "groupBy" as const,
          title: "Iнтервал",
          options: groupByRange,
        }
      : undefined,
  ].filter(notUndefined);

  return filters;
};

const getDefaultValue = (value: string | null | undefined) => {
  if (value === undefined || value === null) return EMPTY_VAULE;
  return value;
};

export const resolveFilter = <T extends Record<string, unknown>>(filter: T) => {
  return Object.entries(filter).reduce(
    (acc, [key, value]) => {
      acc[key as keyof T] =
        value === EMPTY_VAULE ? undefined : (value as T[keyof T]);
      return acc;
    },
    {} as { [K in keyof T]: T[K] | undefined },
  );
};

export type GeneralFilterProps = ComponentProps<typeof Card> & {
  withGroupBy?: boolean;
};
export const GeneralFilter: FC<GeneralFilterProps> = ({
  withGroupBy = false,
  ...props
}) => {
  const { t } = useTranslation("filters");
  const filters = useGetGeneralFilters(withGroupBy);
  const { dispatch, filter } = useGeneralFilter();

  return (
    <Card
      shadow="md"
      flexDirection="row"
      gap={"$3"}
      alignItems="flex-end"
      paddingVertical={"$3"}
      paddingHorizontal={"$6"}
      data-id="general-filter-card"
      {...props}
    >
      <XStack
        data-id="general-filter-container"
        gap={"$3"}
        flex={filters.length}
      >
        {filters.map(({ name, title, options }) => {
          return (
            <YStack
              key={name}
              flex={1}
              width={getFractionalWidth(filters.length)}
            >
              <Select
                data-id={`general-filter-${name}`}
                value={getDefaultValue(filter[name])}
                onValueChange={(val) => {
                  dispatch({
                    type: "SET_FILTER",
                    filterName: name,
                    value: val,
                  });
                }}
                label={title}
                items={options as { name: string; value: string }[]}
                trigger={title}
              />
            </YStack>
          );
        })}
      </XStack>
      <XStack>
        <Button
          data-id="general-filter-reset-button"
          onPress={() => {
            dispatch({ type: "RESET_FILTER" });
          }}
          variant="gray"
          size="sm"
          rounded="lg"
        >
          {t("reset-filters")}
        </Button>
      </XStack>
    </Card>
  );
};
